import type {Router} from 'vue-router';
import {persistentStorage} from '@/ts/instances/persistant-storage';

/**
 * After logout the browser can restore an authenticated page from its
 * back/forward cache (bfcache) when the user presses Back: the page is served
 * from a snapshot without re-running any auth check, so a stale logged-in view
 * (e.g. Today) is shown even though the session is gone. Interacting with it
 * then bounces to the logged-out landing, on top of rendering it in the
 * previous user's URL language (IPD-2961 #5/#6).
 *
 * On a bfcache restore of a route that requires auth with no active session, do
 * a full navigation to `loggedOutPath` (the patient sign-up or the admin
 * login). "Requires auth" is read from the router's own `meta.auth` (the same
 * source of truth the navigation guard uses) rather than the URL, so public
 * pages that live outside `/public` — e.g. /privacy-policy, /instructions-for-use
 * — are correctly left alone. We replace() to the bare path (rather than reload
 * the restored URL) so the language resolves from the browser default — same as
 * a normal logout — instead of inheriting the restored page's stale locale
 * prefix (a DE user would otherwise land on a DE page).
 */
export function initBfcacheAuthGuard(router: Router, loggedOutPath: string): void {
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) {
      return;
    }
    const requiresAuth = router.currentRoute.value.matched.some((route) => Boolean(route.meta?.auth));
    if (requiresAuth && !persistentStorage.auth?.access_token) {
      window.location.replace(loggedOutPath);
    }
  });
}
